import { type RowData } from "@tanstack/react-table";
import { type AppAccessorColumnDef } from "./tableFeatures";
import getMetricSpec, {
  isKnownMetric,
  metricNameSelector,
  metricToolTipSelector,
} from "../../modules/merchandising/utils/metrics";
import { MetricLabel } from "../i18n/MetricLabel";
import { MetricCell } from "./cells/MetricCell";
import type { FormatOptions } from "../../utils/formatNumber";
import { createAppColumnHelper } from "./tableFeatures";

interface OverrideOptions {
  formatOptions?: Partial<FormatOptions>;
}

const buildMetricColumn = <TData extends RowData>(
  id: string,
  options: OverrideOptions = {},
): AppAccessorColumnDef<TData, number | null> => {
  if (!isKnownMetric(id)) {
    throw new Error(`Metric with id "${id}" not found in metrics definitions`);
  }
  const currentValueDef = getMetricSpec(id);
  if (!["number", "percent", "currency", "percent_goal", "currency_goal"].includes(currentValueDef.format)) {
    throw new Error(`Unsupported format ${currentValueDef.format} for metric "${id}"`);
  }

  const prevAccessor = currentValueDef.prev_accessor;
  const deltaAccessor = currentValueDef.delta_accessor;
  const percentAccessor = currentValueDef.percent_accessor;
  const goalCoverageAccessor = currentValueDef.goal_coverage_accessor;

  const hasComparison = Boolean(prevAccessor || deltaAccessor || percentAccessor);

  const metricFormat = currentValueDef.format;
  const baseFormat =
    metricFormat === "percent_goal" ? "percent"
    : metricFormat === "currency_goal" ? "currency"
    : metricFormat;

  return {
    id,
    // Render the header via a React component so useTranslation can run in scope.
    header: () => <MetricLabel label={metricNameSelector(id)} />,
    accessorFn: currentValueDef.accessor,
    cell: ({ getValue, row }) => (
      <MetricCell
        value={getValue()}
        previousValue={prevAccessor ? prevAccessor(row.original) : undefined}
        deltaValue={deltaAccessor ? deltaAccessor(row.original) : undefined}
        percentValue={percentAccessor ? percentAccessor(row.original) : undefined}
        formatOptions={{
          format: baseFormat as "number" | "percent" | "currency",
          decimalPlaces: currentValueDef.decimalPlaces,
          compact: false,
          ...options.formatOptions,
        }}
        compareHighlight={currentValueDef.compareHighlight}
        goalCoverage={goalCoverageAccessor ? goalCoverageAccessor(row.original) : undefined}
      />
    ),
    minSize: hasComparison ? 120 : 75,
    maxSize: hasComparison ? 260 : 200,
    size: hasComparison ? 160 : 120,
    meta: {
      columnFilterOptions: {
        label: metricNameSelector(id),
        format: baseFormat,
      },
      columnSelectorOptions: {
        label: metricNameSelector(id),
        description: metricToolTipSelector(id),
        grouping: currentValueDef.grouping,
      },
      justify: "end",
      alternateSortingOptions: currentValueDef.alternateSortingOptions,
    },
  };
};

/**
 * Extend @tanstack/table's column helper to build columns from definitions insteand of only custom columns
 */
export function createColumnBuilder<TData extends RowData>() {
  const columnHelper = createAppColumnHelper<TData>();
  return {
    ...columnHelper,
    metric: buildMetricColumn<TData>,
  };
}
